import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useState,
  useMemo,
  useRef,
} from "react";
import { useColorMode, Button, AlertDialog, AlertDialogBody, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogOverlay } from "@chakra-ui/react";
import { getThemeClasses } from "../../../utils/themeUtils";
import { CiEdit } from "react-icons/ci";
import { MdDeleteOutline } from "react-icons/md";
import { FaCheck, FaTimes } from "react-icons/fa";
import { User, Metric } from "./UserManagement";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db, auth } from "../../../firebase/firebase";
import { logToSystem } from "../../../utils/systemLogging";
import Loader from "../../../components/Loader";
import { IconWrapper } from "../../../components/IconWrapper";
import CustomDropdown from "../../../components/CustomDropdown";
import { getKpiPermissions } from "../../../utils/permissions";
import { showToast } from "../../../utils/toastUtils";

interface UserMetricsTableProps {
  user: User;
  metrics: Metric[];
  filteredMetrics: Metric[];
  color: string;
  bg: string;
  setUserMetrics: Dispatch<SetStateAction<{ [userId: string]: Metric[] }>>;
  updateMetric: (userId: string, updatedMetric: Metric) => void;
  deleteMetric: (userId: string, metricId: number) => void;
  organizationId: string;
  currentUserId: string;
  users: User[];
}

const UserMetricsTable: React.FC<UserMetricsTableProps> = ({
  user,
  metrics,
  filteredMetrics,
  setUserMetrics,
  color,
  bg,
  updateMetric,
  deleteMetric,
  organizationId,
  currentUserId,
  users,
}) => {
  const { colorMode } = useColorMode();
  const isDark = colorMode === "dark";
  const theme = getThemeClasses(isDark);
  const [editableId, setEditableId] = useState<number | null>(null);
  const [editMetric, setEditMetric] = useState<Metric | null>(null);
  const [loading, setLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isPermissionModalOpen, setPermissionModalOpen] = useState(false);
  const permissionCancelRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const fetchUserMetrics = async () => {
      setLoading(true);
      try {
        const userKpiRef = doc(
          db,
          `organization/${organizationId}/Metrics/userKpiMetrics`
        );
        const userKpiSnap = await getDoc(userKpiRef);
        if (userKpiSnap.exists()) {
          const userData = userKpiSnap.data();
          const fetchedMetrics = userData.users?.[user.id] || [];
          setUserMetrics({ [user.id]: fetchedMetrics });
        } else {
          setUserMetrics({});
        }
      } catch (error) {
        console.error("❌ Error fetching user metrics:", error);
      }
      setLoading(false);
    };
    fetchUserMetrics();
  }, [organizationId, user.id]);

  const handleEdit = (metric: Metric) => {
    if (kpiPerms.isViewer && metric.createdBy !== kpiPerms.currentUserId) {
      setPermissionModalOpen(true);
      return;
    }
    setEditableId(metric.id);
    setEditMetric({ ...metric });
  };

  const handleDelete = async (userId: string, metricId: string) => {
    if (
      window.confirm(
        "Are you sure you want to delete this metric? This action cannot be undone."
      )
    ) {
      await confirmDelete(userId, metricId);
    }
  };

  const confirmDelete = async (userId: string, metricId: string) => {
    try {
      const userKpiRef = doc(
        db,
        `organization/${organizationId}/Metrics/userKpiMetrics`
      );
      const userKpiSnap = await getDoc(userKpiRef);
      if (userKpiSnap.exists()) {
        const userData = userKpiSnap.data();
        if (userData.users && userData.users[userId]) {
          const updatedUserMetrics = userData.users[userId].filter(
            (metric: Metric) => String(metric.id) !== metricId
          );
          await updateDoc(userKpiRef, {
            [`users.${userId}`]: updatedUserMetrics,
          });
        }
      }
      setUserMetrics((prev) => ({
        ...prev,
        [userId]: prev[userId].filter(
          (metric) => String(metric.id) !== metricId
        ),
      }));

      // Log user metric deletion activity
      try {
        const currentUserAuth = auth.currentUser;
        if (currentUserAuth && organizationId) {
          const userRefAuth = doc(db, "users", currentUserAuth.uid);
          const userSnapAuth = await getDoc(userRefAuth);

          if (userSnapAuth.exists()) {
            const authUserData = userSnapAuth.data();

            // Find the deleted metric to get its details
            const deletedMetric = metrics.find(m => String(m.id) === metricId);

            await logToSystem(organizationId, "actions", {
              type: "ACTIVITY",
              user: `${authUserData.firstName} ${authUserData.lastName}`,
              email: authUserData.email,
              action: "KPI Management",
              details: deletedMetric
                ? `Deleted KPI metric ${deletedMetric.metric} for user ${user.firstName} ${user.lastName}`
                : `Deleted KPI metric for user ${user.firstName} ${user.lastName}`,
              status: "success",
            });
          }
        }
      } catch (logError) {
        console.error("Error logging user KPI metric deletion:", logError);
      }
    } catch (error) {
      console.error("❌ Error deleting metric:", error);
    }
  };

  const currentUser = useMemo(
    () => users.find((u) => u.id === currentUserId),
    [users, currentUserId]
  );
  const kpiPerms = useMemo(() => getKpiPermissions(currentUser), [currentUser]);

  const handleSave = async () => {
    if (!editMetric) return;

    // Validation: No fields should be empty
    if (!editMetric.metric.trim() || !editMetric.target.trim() || !editMetric.actual.trim()) {
      showToast({ title: "All fields (Metric, Target, Actual) must be filled.", status: "warning" });
      return;
    }

    setIsUpdating(true);
    if (editMetric) {
      try {
        // Update user's copy of the metric
        const userKpiRef = doc(
          db,
          `organization/${organizationId}/Metrics/userKpiMetrics`
        );
        const userKpiSnap = await getDoc(userKpiRef);
        if (!userKpiSnap.exists()) {
          return;
        }
        const userData = userKpiSnap.data();
        const userMetrics = userData.users?.[user.id] || [];
        const updatedUserMetrics = userMetrics.map((metric: Metric) =>
          metric.id === editMetric.id ? { ...editMetric } : metric
        );
        await updateDoc(userKpiRef, {
          [`users.${user.id}`]: updatedUserMetrics,
        });

        // If it's a common metric, also update the main common metrics collection
        // BUT only if the user has edit permission (admin/editor or creator of the common metric)
        if (editMetric.type === "common" && kpiPerms.canEdit(editMetric)) {
          const commonMetricsRef = doc(
            db,
            `organization/${organizationId}/Metrics/commonKpiMetrics`
          );
          const commonMetricsSnap = await getDoc(commonMetricsRef);

          if (commonMetricsSnap.exists()) {
            const commonData = commonMetricsSnap.data();
            const updatedCommonMetrics = commonData.metrics.map(
              (metric: Metric) =>
                metric.id === editMetric.id ? { ...editMetric } : metric
            );
            await updateDoc(commonMetricsRef, {
              metrics: updatedCommonMetrics,
            });
          }
        }

        setUserMetrics((prev) => ({
          ...prev,
          [user.id]: updatedUserMetrics,
        }));

        // Log user metric update activity
        try {
          const currentUserAuth = auth.currentUser;
          if (currentUserAuth && organizationId) {
            const userRef = doc(db, "users", currentUserAuth.uid);
            const userSnap = await getDoc(userRef);

            if (userSnap.exists()) {
              const authUserData = userSnap.data();

              // Find original metric to track changes
              const originalMetric = metrics.find(m => m.id === editMetric.id);
              const changes: string[] = [];

              if (originalMetric) {
                if (originalMetric.metric !== editMetric.metric) changes.push(`Name: "${originalMetric.metric}" -> "${editMetric.metric}"`);
                if (originalMetric.target !== editMetric.target) changes.push(`Target: ${originalMetric.target} -> ${editMetric.target}`);
                if (originalMetric.actual !== editMetric.actual) changes.push(`Actual: ${originalMetric.actual} -> ${editMetric.actual}`);
                if (originalMetric.status !== editMetric.status) changes.push(`Status: ${originalMetric.status} -> ${editMetric.status}`);
              }

              const changesList = changes.length > 0 ? changes.join('; ') : 'No changes';

              await logToSystem(organizationId, "actions", {
                type: "ACTIVITY",
                user: `${authUserData.firstName} ${authUserData.lastName}`,
                email: authUserData.email,
                action: "KPI Management",
                details: `Updated KPI metric ${editMetric.metric} for user ${user.firstName} ${user.lastName}. Changes: ${changesList}`,
                status: "success",
              });
            }
          }
        } catch (logError) {
          console.error("Error logging user KPI metric update:", logError);
        }

        setEditableId(null);
        setEditMetric(null);
      } catch (error) {
        console.error("❌ Error updating user metric:", error);
      } finally {
        setIsUpdating(false);
      }
    }
  };

  return (
    <div className="flex flex-col">
      {loading ? (
        <Loader message="Fetching user KPI Metrics..." color="#B59143" />
      ) : (
        <div className={`${isDark ? 'bg-[#1A1C1F] border border-[#2E2F33]' : 'bg-white border border-gray-200'} rounded-lg overflow-hidden`}>
          <div className="overflow-y-auto" style={{ maxHeight: "62vh" }}>
            <table className={`min-w-full divide-y ${isDark ? 'divide-[#2E2F33]' : 'divide-gray-100'} text-sm`}>
              <thead className={`${isDark ? 'bg-[#14161B]' : 'bg-gray-50'}`}>
                <tr>
                  <th className={`px-4 py-2 text-left font-semibold tracking-wide ${isDark ? 'text-white' : 'text-gray-700'}`}>
                    Metric
                  </th>
                  <th className={`px-4 py-2 text-left font-semibold tracking-wide ${isDark ? 'text-white' : 'text-gray-700'}`}>
                    Description
                  </th>
                  <th className={`px-4 py-2 text-center font-semibold tracking-wide ${isDark ? 'text-white' : 'text-gray-700'}`}>
                    Target
                  </th>
                  <th className={`px-4 py-2 text-center font-semibold tracking-wide ${isDark ? 'text-white' : 'text-gray-700'}`}>
                    Actual
                  </th>
                  <th className={`px-4 py-2 text-center font-semibold tracking-wide ${isDark ? 'text-white' : 'text-gray-700'}`}>
                    Status
                  </th>
                  {!kpiPerms.isViewer && (
                  <th className={`px-4 py-2 text-center font-semibold tracking-wide ${isDark ? 'text-white' : 'text-gray-700'}`}>
                    Actions
                  </th>
                  )}
                </tr>
              </thead>
              <tbody className={`${isDark ? 'bg-[#1A1C1F]' : 'bg-white'} divide-y ${isDark ? 'divide-[#2E2F33]' : 'divide-gray-100'}`}>
                {filteredMetrics.length === 0 ? (
                  <tr>
                    <td
                      colSpan={kpiPerms.isViewer ? 5 : 6}
                      className={`text-center py-8 ${isDark ? 'text-gray-400' : 'text-gray-500'} text-xs`}
                    >
                      No metrics found.
                    </td>
                  </tr>
                ) : (
                  filteredMetrics.map((metric, idx) => {
                    const canEditAll = kpiPerms.canEdit(metric);
                    const canDeleteAll = kpiPerms.canDelete(metric);
                    const restrictionTitle = !canEditAll ? "Only the creator or an admin can edit this field" : "";

                    return (
                      <tr key={metric.id} className={`${isDark ? 'hover:bg-[#2A3441]' : 'hover:bg-gray-50'}`}>
                        {editableId === metric.id ? (
                          <>
                            <td className="px-4 py-3 text-left align-middle" title={restrictionTitle}>
                              <input
                                className={`w-full px-2 py-1 text-xs rounded transition-all duration-200 text-left outline-none ${isDark ? 'bg-[#0B0B0F] text-white border border-[#2E2F33] focus:ring-2 focus:ring-[#B59143] focus:border-[#B59143] hover:border-[#B59143]' : 'bg-white text-[#111827] border border-[#E5E7EB] focus:ring-[#B59143] focus:border-[#B59143] hover:border-[#B59143]'}`}
                                value={editMetric?.metric}
                                disabled={!canEditAll}
                                onChange={(e) =>
                                  setEditMetric({
                                    ...editMetric!,
                                    metric: e.target.value,
                                  })
                                }
                                style={!canEditAll ? { opacity: 0.6, cursor: 'not-allowed' } : {}}
                              />
                            </td>
                            <td className="px-4 py-3 text-left align-middle" title={restrictionTitle}>
                              <input
                                className={`w-full px-2 py-1 text-xs rounded transition-all duration-200 text-left outline-none ${isDark ? 'bg-[#0B0B0F] text-white border border-[#2E2F33] focus:ring-2 focus:ring-[#B59143] focus:border-[#B59143] hover:border-[#B59143]' : 'bg-white text-[#111827] border border-[#E5E7EB] focus:ring-[#B59143] focus:border-[#B59143] hover:border-[#B59143]'}`}
                                value={editMetric?.description || ""}
                                disabled={!canEditAll}
                                onChange={(e) =>
                                  setEditMetric({
                                    ...editMetric!,
                                    description: e.target.value,
                                  })
                                }
                                placeholder="No description provided"
                                style={!canEditAll ? { opacity: 0.6, cursor: 'not-allowed' } : {}}
                              />
                            </td>
                            <td className="px-4 py-3 align-middle" title={restrictionTitle}>
                              <input
                                className={`w-full px-2 py-1 text-xs rounded transition-all duration-200 text-center outline-none ${isDark ? 'bg-[#0B0B0F] text-white border border-[#2E2F33] focus:ring-2 focus:ring-[#B59143] focus:border-[#B59143] hover:border-[#B59143]' : 'bg-white text-[#111827] border border-[#E5E7EB] focus:ring-[#B59143] focus:border-[#B59143] hover:border-[#B59143]'}`}
                                value={editMetric?.target}
                                disabled={!canEditAll}
                                onChange={(e) =>
                                  setEditMetric({
                                    ...editMetric!,
                                    target: e.target.value,
                                  })
                                }
                                style={!canEditAll ? { opacity: 0.6, cursor: 'not-allowed' } : {}}
                              />
                            </td>
                            <td className="px-4 py-3 align-middle">
                              <input
                                className={`w-full px-2 py-1 text-xs rounded transition-all duration-200 text-center outline-none ${isDark ? 'bg-[#0B0B0F] text-white border border-[#2E2F33] focus:ring-2 focus:ring-[#B59143] focus:border-[#B59143] hover:border-[#B59143]' : 'bg-white text-[#111827] border border-[#E5E7EB] focus:ring-[#B59143] focus:border-[#B59143] hover:border-[#B59143]'}`}
                                value={editMetric?.actual}
                                onChange={(e) =>
                                  setEditMetric({
                                    ...editMetric!,
                                    actual: e.target.value,
                                  })
                                }
                              />
                            </td>
                            <td className="px-4 py-3 align-middle">
                              <CustomDropdown
                                value={editMetric?.status || "⚠️"}
                                onChange={(value) =>
                                  setEditMetric({
                                    ...editMetric!,
                                    status: value,
                                  })
                                }
                                isDark={isDark}
                                className="w-full px-2 py-1 text-xs rounded transition-all duration-200 text-center outline-none"
                                options={[
                                  { value: "✅", label: "✅ Completed" },
                                  { value: "⚠️", label: "⚠️ In Progress" }
                                ]}
                              />
                            </td>
                            {!kpiPerms.isViewer && (
                            <td className="px-4 py-3 align-middle">
                              <button
                                className={`inline-flex items-center justify-center p-1 rounded-full ${isDark ? 'text-[#B59143] hover:bg-[#2A3441] focus:ring-[#B59143]' : 'text-[#B59143] hover:bg-[#FFF7E6] focus:ring-[#B59143]'} focus:outline-none focus:ring-2 focus:ring-opacity-50`}
                                onClick={handleSave}
                                disabled={isUpdating}
                                title="Save"
                              >
                                {isUpdating ? (
                                  <span className="w-4 h-4 border-2 border-purple-600 border-b-transparent rounded-full animate-spin inline-block" />
                                ) : (
                                  <IconWrapper icon={FaCheck} size={16} />
                                )}
                              </button>
                              <button
                                className={`inline-flex items-center justify-center p-1 rounded-full ml-2 ${isDark ? 'text-[#B59143] hover:bg-[#2A3441] focus:ring-[#B59143]' : 'text-[#B59143] hover:bg-[#FFF7E6] focus:ring-[#B59143]'} focus:outline-none focus:ring-2 focus:ring-opacity-50`}
                                onClick={() => setEditableId(null)}
                                title="Cancel"
                              >
                                <IconWrapper icon={FaTimes} size={16} />
                              </button>
                            </td>
                            )}
                          </>
                        ) : (
                          <>
                            <td className="px-4 py-3 text-left align-middle">
                              {metric.metric}
                            </td>
                            <td className="px-4 py-3 text-left align-middle">
                              <span className={`${isDark ? 'text-gray-300' : ''}`}>{metric.description || "No description provided"}</span>
                            </td>
                            <td className="px-4 py-3 text-center align-middle">
                              {metric.target}
                            </td>
                            <td className="px-4 py-3 text-center align-middle">
                              {metric.actual}
                            </td>
                            <td className="px-4 py-3 text-center align-middle">
                              {metric.status}
                            </td>
                            {!kpiPerms.isViewer && (
                            <td className="px-4 py-3 text-center align-middle">
                              <div className="flex justify-center items-center gap-1">
                                <button
                                  className={`inline-flex items-center p-1 rounded-full ${isDark ? 'text-[#B59143] hover:bg-[#2A3441] focus:ring-[#B59143]' : 'text-[#B59143] hover:bg-[#FFF7E6] focus:ring-[#B59143]'} focus:outline-none focus:ring-2 focus:ring-opacity-50`}
                                  onClick={() => handleEdit(metric)}
                                  title="Edit"
                                >
                                  <IconWrapper icon={CiEdit} size={16} />
                                </button>
                                {canDeleteAll && (
                                  <button
                                    className={`inline-flex items-center p-1 rounded-full ${isDark ? 'text-[#B59143] hover:bg-[#2A3441] focus:ring-[#B59143]' : 'text-[#B59143] hover:bg-[#FFF7E6] focus:ring-[#B59143]'} focus:outline-none focus:ring-2 focus:ring-opacity-50`}
                                    onClick={() => handleDelete(user.id, String(metric.id))}
                                    title="Delete"
                                  >
                                    <IconWrapper icon={MdDeleteOutline} size={16} />
                                  </button>
                                )}
                              </div>
                            </td>
                            )}
                          </>
                        )}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <AlertDialog
        isOpen={isPermissionModalOpen}
        leastDestructiveRef={permissionCancelRef}
        onClose={() => setPermissionModalOpen(false)}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Restricted Access
            </AlertDialogHeader>
            <AlertDialogBody>
              You can only edit KPI metrics created by you. This metric was created by another user.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={permissionCancelRef} onClick={() => setPermissionModalOpen(false)}>
                OK
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </div>
  );
};

export default UserMetricsTable;

